import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useAppContext } from "../../../../AppContext";
import { useAuthentication } from "../../../../common/infrastructure/hooks/useAuthentication";
import { IPreReservationFormSubmission } from "../../../../common/preReservationForms/models";
import {
  WizardStep,
  IAppointmentWizardState,
  IService,
  IEmployeeAvailability,
  IAvailableTimeSlot,
} from "../models";
import {
  useAppointmentServices,
  useAppointmentEmployees,
  useAnyEmployeeFirstAvailableDay,
} from "../hooks";
import {
  useAppointmentWizardApi,
  createAppointmentWizardRequest,
  IAppointmentWizardRequest,
} from "./useAppointmentWizardApi";

const STEP_ORDER: WizardStep[] = [
  "start",
  "services",
  "employees",
  "datetime",
  "login",
  "confirmation",
  "success",
];

const isValidStep = (step: string | null): step is WizardStep => {
  return step !== null && STEP_ORDER.includes(step as WizardStep);
};

export const useAppointmentWizard = () => {
  const { selectedStudio } = useAppContext();
  const authHook = useAuthentication();
  const [pendingAuthRedirect, setPendingAuthRedirect] = useState(false);
  const restorationProcessedRef = useRef(false);
  const isPopstateNavigationRef = useRef(false);
  const initializedFromUrlRef = useRef(false);
  const currentStepRef = useRef<WizardStep>("start");

  // Wizard state
  const [wizardState, setWizardState] = useState<IAppointmentWizardState>({
    currentStep: "start",
    stepIndex: 0,
    totalSteps: STEP_ORDER.length,
    isTransitioning: false,
    canNavigateBack: false,
    canNavigateForward: true,
    selection: {
      service: null,
      employee: null,
      date: null,
      time: null,
      totalPrice: 0,
    },
    isLoading: false,
    error: null,
    bookingId: null,
  });

  // Initialize step from URL on mount and set up browser history
  useEffect(() => {
    // Get step from URL on initial mount
    if (!initializedFromUrlRef.current) {
      initializedFromUrlRef.current = true;
      const urlParams = new URLSearchParams(window.location.search);
      const stepFromUrl = urlParams.get("step");

      if (isValidStep(stepFromUrl) && stepFromUrl !== "start") {
        // Restore step from URL - but only if we have a valid step
        // For now, just go to start since we don't have selection data
        // The URL step is useful for browser back/forward navigation
        const url = new URL(window.location.href);
        url.searchParams.set("step", "start");
        window.history.replaceState({ wizardStep: "start" }, "", url.toString());
      } else {
        // Set initial URL state
        const url = new URL(window.location.href);
        url.searchParams.set("step", "start");
        window.history.replaceState({ wizardStep: "start" }, "", url.toString());
      }
    }

    // Handle browser back/forward navigation
    const handlePopState = (event: PopStateEvent) => {
      const stepFromState = event.state?.wizardStep;

      // If navigating back from success step, reset the entire form and go to start
      if (currentStepRef.current === "success") {
        isPopstateNavigationRef.current = true;

        // Reset wizard to initial state
        setWizardState({
          currentStep: "start",
          stepIndex: 0,
          totalSteps: STEP_ORDER.length,
          isTransitioning: false,
          canNavigateBack: false,
          canNavigateForward: true,
          selection: {
            service: null,
            employee: null,
            date: null,
            time: null,
            totalPrice: 0,
          },
          isLoading: false,
          error: null,
          bookingId: null,
        });

        // Update URL to start
        const url = new URL(window.location.href);
        url.searchParams.set("step", "start");
        window.history.replaceState({ wizardStep: "start" }, "", url.toString());

        setTimeout(() => {
          isPopstateNavigationRef.current = false;
        }, 0);
        return;
      }

      if (isValidStep(stepFromState)) {
        // Mark that we're handling a popstate navigation
        isPopstateNavigationRef.current = true;

        // Navigate to the step from browser history
        setWizardState((prev) => {
          const stepIndex = STEP_ORDER.indexOf(stepFromState);
          return {
            ...prev,
            currentStep: stepFromState,
            stepIndex,
            canNavigateBack: stepIndex > 0,
            canNavigateForward: stepIndex < STEP_ORDER.length - 1,
            isTransitioning: false,
          };
        });

        // Reset the flag after state update
        setTimeout(() => {
          isPopstateNavigationRef.current = false;
        }, 0);
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  // Keep currentStepRef in sync with wizard state
  useEffect(() => {
    currentStepRef.current = wizardState.currentStep;
  }, [wizardState.currentStep]);

  // Fetch services from backend (now with categories)
  const {
    services: availableServices,
    categories: serviceCategories,
    servicesByCategory,
    isLoading: servicesLoading,
  } = useAppointmentServices(selectedStudio?.id);

  // Fetch employees based on selected service
  const { employees: availableEmployees, isLoading: employeesLoading } = useAppointmentEmployees(
    wizardState.selection.service?.id,
    selectedStudio?.id
  );

  // Get first available day for "any employee" selection
  const {
    firstAvailableDate: anyEmployeeFirstAvailableDate,
    selectedEmployee: anyEmployeeFirstAvailableEmployee,
    hasAnyAvailability: anyEmployeeHasAvailability,
  } = useAnyEmployeeFirstAvailableDay(
    availableEmployees,
    wizardState.selection.service?.id,
    selectedStudio?.id
  );

  // Appointment creation mutation
  const appointmentWizardMutation = useAppointmentWizardApi();

  // Helper to update URL with current step
  const updateUrlWithStep = useCallback((step: WizardStep, shouldPushState: boolean) => {
    // Don't update URL for success step (it's a terminal state)
    if (step === "success") return;

    const url = new URL(window.location.href);
    url.searchParams.set("step", step);

    if (shouldPushState) {
      window.history.pushState({ wizardStep: step }, "", url.toString());
    } else {
      window.history.replaceState({ wizardStep: step }, "", url.toString());
    }
  }, []);

  // Step navigation
  const goToStep = useCallback(
    (step: WizardStep, options?: { skipHistoryPush?: boolean }) => {
      // Get fresh auth values
      const { isAuthenticated, currentUser } = authHook;
      const stepIndex = STEP_ORDER.indexOf(step);
      if (stepIndex === -1) return;

      // Check auth requirement at confirmation step
      if (step === "confirmation" && !isAuthenticated && !currentUser?.id) {
        // Go to login step instead of external login page
        step = "login";
      }

      const finalStepIndex = STEP_ORDER.indexOf(step);

      setWizardState((prev) => ({
        ...prev,
        currentStep: step,
        stepIndex: finalStepIndex,
        canNavigateBack: finalStepIndex > 0,
        canNavigateForward: finalStepIndex < STEP_ORDER.length - 1,
        isTransitioning: false,
      }));

      // Update browser history (unless this navigation came from popstate)
      if (!options?.skipHistoryPush && !isPopstateNavigationRef.current) {
        updateUrlWithStep(step, true);
      }
    },
    [authHook, updateUrlWithStep]
  );

  const nextStep = useCallback(() => {
    const { isAuthenticated, currentUser } = authHook;
    const currentStep = wizardState.currentStep;
    let nextIndex = wizardState.stepIndex + 1;

    // Skip employee selection if there's only one real employee (excluding "Any professional")
    const realEmployees = availableEmployees.filter((emp) => emp.id !== -1);
    if (currentStep === "services" && realEmployees.length === 1) {
      // Auto-select the single real employee
      const singleEmployee = realEmployees[0];
      setWizardState((prev) => {
        const basePrice = prev.selection.service?.price || 0;
        const adjustedPrice = Math.round(
          basePrice * singleEmployee.priceModifier
        );

        return {
          ...prev,
          selection: {
            ...prev.selection,
            employee: singleEmployee,
            totalPrice: adjustedPrice,
            // Reset dependent selections
            date: null,
            time: null,
          },
        };
      });

      // Skip to datetime step (employees step is at index 2, datetime is at index 3)
      nextIndex = wizardState.stepIndex + 2;
    }

    // Skip login step if user is already authenticated
    if (nextIndex < STEP_ORDER.length && STEP_ORDER[nextIndex] === "login") {
      if (isAuthenticated && currentUser) {
        nextIndex = nextIndex + 1; // Skip to confirmation step
      }
    }

    if (nextIndex < STEP_ORDER.length) {
      goToStep(STEP_ORDER[nextIndex]);
    }
  }, [
    wizardState.stepIndex,
    wizardState.currentStep,
    availableEmployees,
    goToStep,
    authHook,
  ]);

  const previousStep = useCallback(() => {
    const { isAuthenticated } = authHook;
    const currentStep = wizardState.currentStep;
    let prevIndex = wizardState.stepIndex - 1;

    // Skip employee selection step when going back if there's only one real employee
    const realEmployees = availableEmployees.filter((emp) => emp.id !== -1);
    if (currentStep === "datetime" && realEmployees.length === 1) {
      // Skip back to services step (employees step is at index 2, services is at index 1)
      prevIndex = wizardState.stepIndex - 2;
    }

    // Skip login step when going back if user is already authenticated
    if (
      prevIndex >= 0 &&
      STEP_ORDER[prevIndex] === "login" &&
      isAuthenticated
    ) {
      prevIndex = prevIndex - 1; // Skip back to datetime step
    }

    if (prevIndex >= 0) {
      goToStep(STEP_ORDER[prevIndex]);
    }
  }, [
    wizardState.stepIndex,
    wizardState.currentStep,
    availableEmployees,
    goToStep,
    authHook,
  ]);

  // Selection methods
  const selectService = useCallback((service: IService) => {
    setWizardState((prev) => ({
      ...prev,
      selection: {
        ...prev.selection,
        service,
        totalPrice: service.price,
        // Reset dependent selections
        employee: null,
        date: null,
        time: null,
      },
    }));
  }, []);

  const selectEmployee = useCallback(
    (employee: IEmployeeAvailability) => {
      // If "Any professional" is selected, use the first available day logic
      if (employee.id === -1) {
        if (
          anyEmployeeFirstAvailableEmployee &&
          anyEmployeeFirstAvailableDate &&
          anyEmployeeHasAvailability
        ) {
          setWizardState((prev) => {
            const basePrice = prev.selection.service?.price || 0;
            const adjustedPrice = Math.round(
              basePrice * anyEmployeeFirstAvailableEmployee.priceModifier
            );

            return {
              ...prev,
              selection: {
                ...prev.selection,
                employee: anyEmployeeFirstAvailableEmployee,
                date: anyEmployeeFirstAvailableDate,
                totalPrice: adjustedPrice,
                // Reset time selection since we're changing the date
                time: null,
              },
            };
          });

          // Auto-advance to datetime step for "any professional"
          setTimeout(() => goToStep("datetime"), 100);
        } else {
          // Fallback: select the first real employee if no availability data yet
          const firstRealEmployee = availableEmployees.find(
            (emp) => emp.id !== -1
          );
          if (firstRealEmployee) {
            setWizardState((prev) => {
              const basePrice = prev.selection.service?.price || 0;
              const adjustedPrice = Math.round(
                basePrice * firstRealEmployee.priceModifier
              );

              return {
                ...prev,
                selection: {
                  ...prev.selection,
                  employee: firstRealEmployee,
                  totalPrice: adjustedPrice,
                  // Reset dependent selections
                  date: null,
                  time: null,
                },
              };
            });

            // Auto-advance to datetime step for "any professional"
            setTimeout(() => goToStep("datetime"), 100);
          }
        }
        return;
      }

      // Regular employee selection
      setWizardState((prev) => {
        const basePrice = prev.selection.service?.price || 0;
        const adjustedPrice = Math.round(basePrice * employee.priceModifier);

        return {
          ...prev,
          selection: {
            ...prev.selection,
            employee,
            totalPrice: adjustedPrice,
            // Reset dependent selections
            date: null,
            time: null,
          },
        };
      });
    },
    [
      availableEmployees,
      goToStep,
      anyEmployeeFirstAvailableEmployee,
      anyEmployeeFirstAvailableDate,
      anyEmployeeHasAvailability,
    ]
  );

  const selectDateTime = useCallback((date: Date, time: string) => {
    setWizardState((prev) => ({
      ...prev,
      selection: {
        ...prev.selection,
        date,
        time,
      },
    }));
  }, []);

  const selectDateOnly = useCallback((date: Date) => {
    // Normalize the date to avoid timezone issues
    const dateKey = date.toISOString().split("T")[0];
    const normalizedDate = new Date(dateKey + "T12:00:00");
    
    setWizardState((prev) => ({
      ...prev,
      selection: {
        ...prev.selection,
        date: normalizedDate,
        // Reset time when date changes
        time: null,
      },
    }));
  }, []);

  // "Any professional" logic - simplified version for now (can be enhanced later)
  const selectAnyProfessional = useCallback(
    (date: Date) => {
      // For now, just select the first available employee
      if (availableEmployees.length > 0) {
        const firstEmployee = availableEmployees[0];
        setWizardState((prev) => {
          const basePrice = prev.selection.service?.price || 0;
          const adjustedPrice = Math.round(
            basePrice * firstEmployee.priceModifier
          );

          return {
            ...prev,
            selection: {
              ...prev.selection,
              employee: firstEmployee,
              date,
              time: null, // Will be set when time slots are available
              totalPrice: adjustedPrice,
            },
          };
        });

        // Auto-advance to datetime step
        goToStep("datetime");
      }
    },
    [availableEmployees, goToStep]
  );

  // Jump to first available day using bulk availability data
  const jumpToFirstAvailableDay = useCallback(
    (dateAvailabilityMap?: Map<string, any>) => {
      if (!dateAvailabilityMap || dateAvailabilityMap.size === 0) {
        // Fallback to tomorrow if no availability data
        const currentDate = new Date();
        currentDate.setDate(currentDate.getDate() + 1);
        const dateKey = currentDate.toISOString().split("T")[0];
        const tomorrow = new Date(dateKey + "T12:00:00");
        selectDateOnly(tomorrow);
        return true; // Assume success if no data to check
      }

      // Find the first day with available time slots
      // Iterate over the days returned by the backend (sorted by date)
      const sortedDateKeys = Array.from(dateAvailabilityMap.keys()).sort();
      const todayKey = new Date().toISOString().split("T")[0];

      for (const dateKey of sortedDateKeys) {
        // Skip today, start from tomorrow
        if (dateKey <= todayKey) continue;

        const availability = dateAvailabilityMap.get(dateKey);

        if (
          availability?.hasSlots &&
          availability.timeSlots.some(
            (slot: IAvailableTimeSlot) => slot.isAvailable
          )
        ) {
          const checkDate = new Date(dateKey + "T12:00:00");
          selectDateOnly(checkDate);
          return true; // Found an available day
        }
      }

      // If no available day found, don't select any day and return false
      return false;
    },
    [selectDateOnly]
  );

  // Check if there are any available slots in the visible days
  const hasAnyAvailableSlots = useCallback(
    (dateAvailabilityMap?: Map<string, any>) => {
      if (!dateAvailabilityMap || dateAvailabilityMap.size === 0) {
        return true; // Assume true if no data to check
      }

      // Check all days returned by the backend
      for (const [, availability] of dateAvailabilityMap) {
        if (
          availability?.hasSlots &&
          availability.timeSlots.some(
            (slot: IAvailableTimeSlot) => slot.isAvailable
          )
        ) {
          return true;
        }
      }

      return false;
    },
    []
  );

  // Validation helpers
  const canProceedFromStep = useMemo(() => {
    const { selection, currentStep } = wizardState;

    switch (currentStep) {
      case "start":
        return true;
      case "services":
        return !!selection.service;
      case "employees":
        return !!selection.employee;
      case "datetime":
        return !!selection.date && !!selection.time;
      case "confirmation":
        return (
          !!selection.service &&
          !!selection.employee &&
          !!selection.date &&
          !!selection.time
        );
      default:
        return false;
    }
  }, [wizardState]);

  // Loading states
  const setLoading = useCallback((loading: boolean) => {
    setWizardState((prev) => ({
      ...prev,
      isLoading: loading,
    }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setWizardState((prev) => ({
      ...prev,
      error,
    }));
  }, []);

  const triggerAuthRedirect = useCallback(() => {
    setPendingAuthRedirect(true);
  }, []);

  // Validate and match pending service ID when services load
  useEffect(() => {
    const pendingServiceId = (wizardState as any).pendingServiceId;

    if (
      pendingServiceId &&
      availableServices.length > 0 &&
      !wizardState.selection.service
    ) {
      const matchedService = availableServices.find(
        (s) => s.id === pendingServiceId
      );

      if (matchedService) {
        setWizardState(
          (prev) =>
            ({
              ...prev,
              selection: {
                ...prev.selection,
                service: matchedService,
              },
              pendingServiceId: undefined,
            } as any)
        );
      } else {
        // Service not found or unavailable - restart wizard
        setWizardState(
          (prev) =>
            ({
              ...prev,
              currentStep: "start",
              stepIndex: 0,
              canNavigateBack: false,
              canNavigateForward: true,
              selection: {
                service: null,
                employee: null,
                date: null,
                time: null,
                totalPrice: 0,
              },
              pendingServiceId: undefined,
              pendingEmployeeId: undefined,
            } as any)
        );
      }
    }
  }, [availableServices, wizardState.selection.service]);

  // Validate and match pending employee ID when employees load
  useEffect(() => {
    const pendingEmployeeId = (wizardState as any).pendingEmployeeId;

    if (
      pendingEmployeeId &&
      availableEmployees.length > 0 &&
      !wizardState.selection.employee
    ) {
      const matchedEmployee = availableEmployees.find(
        (e) => e.id === pendingEmployeeId
      );

      if (matchedEmployee) {
        setWizardState(
          (prev) =>
            ({
              ...prev,
              selection: {
                ...prev.selection,
                employee: matchedEmployee,
              },
              pendingEmployeeId: undefined,
            } as any)
        );
      } else {
        setWizardState(
          (prev) =>
            ({
              ...prev,
              currentStep: "start",
              stepIndex: 0,
              canNavigateBack: false,
              canNavigateForward: true,
              selection: {
                service: null,
                employee: null,
                date: null,
                time: null,
                totalPrice: 0,
              },
              pendingServiceId: undefined,
              pendingEmployeeId: undefined,
            } as any)
        );
      }
    }
  }, [
    availableEmployees,
    wizardState.selection.employee,
    wizardState.selection.service,
  ]);

  // Watch for authentication changes and auto-proceed from login
  useEffect(() => {
    if (
      pendingAuthRedirect &&
      authHook.isAuthenticated &&
      authHook.currentUser &&
      wizardState.currentStep === "login"
    ) {
      setPendingAuthRedirect(false);
      setTimeout(() => {
        goToStep("confirmation");
      }, 50);
    }
  }, [
    authHook.isAuthenticated,
    authHook.currentUser,
    wizardState.currentStep,
    pendingAuthRedirect,
    goToStep,
  ]);

  // Simplified effect: Restore wizard state from localStorage after authentication
  useEffect(() => {
    // Only attempt restoration if user just became authenticated and we haven't processed yet
    if (
      authHook.isAuthenticated &&
      authHook.currentUser &&
      !restorationProcessedRef.current
    ) {
      const backupState = localStorage.getItem("wizardStateBackup");
      if (backupState) {
        try {
          const parsedBackup = JSON.parse(backupState);

          // Check if backup has expired (10 minutes)
          const now = Date.now();
          const backupAge = now - (parsedBackup.timestamp || 0);
          const isExpired = backupAge > 10 * 60 * 1000; // 10 minutes

          if (isExpired) {
            return;
          }

          if (parsedBackup.selection?.service?.id) {
            restorationProcessedRef.current = true; // Mark as processed

            // Set basic state with IDs for later validation
            setWizardState(
              (prev) =>
                ({
                  ...prev,
                  currentStep: "confirmation", // Go directly to confirmation since user is authenticated
                  stepIndex: STEP_ORDER.indexOf("confirmation"),
                  canNavigateBack: true,
                  canNavigateForward: true,
                  selection: {
                    ...prev.selection,
                    totalPrice: parsedBackup.selection?.totalPrice || 0,
                    date: parsedBackup.selection?.date
                      ? new Date(parsedBackup.selection.date)
                      : null,
                    time: parsedBackup.selection?.time || null,
                  },
                  // Store pending IDs for validation when data loads
                  pendingServiceId: parsedBackup.selection?.service?.id,
                  pendingEmployeeId: parsedBackup.selection?.employee?.id,
                } as any)
            );

          }
        } catch (e) {
          console.error("Failed to parse backup during auth:", e);
          localStorage.removeItem("wizardStateBackup");
        }
      }
    }
  }, [authHook.isAuthenticated, authHook.currentUser]);

  // Create appointment
  const createAppointment = useCallback(async (
    formSubmission: IPreReservationFormSubmission | null = null,
  ) => {
    // Extra safety check - only allow creation on confirmation step
    if (wizardState.currentStep !== "confirmation") {
      return;
    }

    const { selection } = wizardState;

    if (
      !selectedStudio ||
      !selection.service ||
      !selection.employee ||
      !selection.date ||
      !selection.time
    ) {
      setError("Missing required appointment information");
      return;
    }

    // Parse the date and time to create a DateTime
    const selectedDateTime = new Date(selection.date);
    const [hours, minutes] = selection.time.split(":").map(Number);
    selectedDateTime.setHours(hours, minutes, 0, 0);

    const appointmentData: IAppointmentWizardRequest = {
      ServiceId: selection.service.id,
      EmployeeId: selection.employee.id,
      StartTime: selectedDateTime.toISOString(),
      Notes: "", // Could be extended to include notes in the future
      studioId: selectedStudio.id,
      FormSubmission: formSubmission,
    };

    const mutationRequest = createAppointmentWizardRequest(appointmentData);

    setLoading(true);
    setError(null);

    try {
      const response = await appointmentWizardMutation.mutateAsync(
        mutationRequest
      );

      if (response && response.success) {
        setWizardState((prev) => ({
          ...prev,
          bookingId: response.appointmentId.toString(),
        }));
        localStorage.removeItem("wizardStateBackup");
        goToStep("success");
      } else {
        setError(response?.message || "Failed to create appointment");
      }
    } catch (error) {
      // Check if it's an axios error with response data
      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as any;
        console.error("[createAppointment] Axios error details:", {
          status: axiosError.response?.status,
          statusText: axiosError.response?.statusText,
          data: axiosError.response?.data,
          message: axiosError.message,
        });

        // Try to extract a more specific error message
        const backendMessage =
          axiosError.response?.data?.message ||
          axiosError.response?.data ||
          axiosError.message;
        setError(`Failed to create appointment: ${backendMessage}`);
      } else {
        setError("Failed to create appointment. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [
    wizardState,
    selectedStudio,
    appointmentWizardMutation,
    setLoading,
    setError,
    goToStep,
  ]);

  // Progress calculation (adjust for potentially skipped steps)
  const progressPercentage = useMemo(() => {
    const { isAuthenticated } = authHook;
    let effectiveTotalSteps = wizardState.totalSteps;
    let effectiveStepIndex = wizardState.stepIndex;

    // If there's only one real employee, subtract 1 from total steps since employee step is skipped
    const realEmployees = availableEmployees.filter((emp) => emp.id !== -1);
    if (realEmployees.length === 1) {
      effectiveTotalSteps = wizardState.totalSteps - 1;

      // If we're past the employee step, subtract 1 from current index too
      if (wizardState.stepIndex > 2) {
        // employee step is at index 2
        effectiveStepIndex = wizardState.stepIndex - 1;
      }
    }

    // If user is authenticated, subtract 1 from total steps since login step is skipped
    if (isAuthenticated) {
      effectiveTotalSteps = effectiveTotalSteps - 1;

      // If we're past the login step, subtract 1 from current index too
      if (wizardState.stepIndex > 4) {
        // login step is at index 4
        effectiveStepIndex = effectiveStepIndex - 1;
      }
    }

    return Math.round((effectiveStepIndex / (effectiveTotalSteps - 1)) * 100);
  }, [
    wizardState.stepIndex,
    wizardState.totalSteps,
    availableEmployees,
    authHook,
  ]);

  // Available data is calculated at the top of the hook

  return {
    // State
    ...wizardState,
    canProceedFromStep,
    progressPercentage,

    // Data
    availableServices,
    serviceCategories,
    servicesByCategory,
    availableEmployees,
    servicesLoading,
    employeesLoading,
    selectedStudio,
    currentUser: authHook.isAuthenticated ? authHook.currentUser : null,

    // Navigation
    goToStep,
    nextStep,
    previousStep,

    // Selection
    selectService,
    selectEmployee,
    selectDateTime,
    selectDateOnly,
    selectAnyProfessional,
    jumpToFirstAvailableDay,

    // Utils
    setLoading,
    setError,
    createAppointment,
    triggerAuthRedirect,
    hasAnyAvailableSlots,
  };
};
